import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WebCallsAPI from 'dashboard/api/webCalls';
import WebCallClient from 'dashboard/api/channel/voice/webCallClient';
import { useCallsStore } from 'dashboard/stores/calls';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';

/**
 * Places an outbound web call on a conversation.
 *
 * Extracted from WebCallButton so the call-back on a missed web call goes down
 * the same path: it used to fall through to the Twilio contact dial, which
 * 404s on an account with no Twilio voice inbox even though the customer had
 * just reached that account over a web call.
 *
 * The room join is the fragile half — create() can succeed and connect() still
 * fail, leaving a ringing call the agent never reached — so the unwind lives
 * here too rather than in each caller.
 */
export const useWebCall = () => {
  const { t } = useI18n();
  const callsStore = useCallsStore();
  const isStarting = ref(false);

  const start = async conversationId => {
    if (isStarting.value || !conversationId) return null;
    isStarting.value = true;

    // Captured as soon as the backend call record exists, so the catch block
    // below knows whether it needs to unwind the store/room it created.
    let createdCall = null;
    try {
      const { data } = await WebCallsAPI.create(conversationId);
      createdCall = data.call;
      callsStore.addCall({
        callSid: data.call.provider_call_id,
        callId: data.call.id,
        provider: VOICE_CALL_PROVIDERS.WEB,
        conversationId: data.call.conversation_id,
        callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
        // to_number is the contact's phone, not a name — leave name blank so
        // CallCard's getCallInfo falls back to the conversation's contact name
        // instead of showing a phone number where a name belongs.
        caller: { name: '', phone: data.call.to_number || '' },
        // Lets the agent hand the link over if the ring goes unseen.
        callLinkUrl: data.call.call_link_url || '',
      });
      // Stay non-active while ringing — FloatingCallWidget renders the OUTGOING
      // card (no timer). voice_call.outbound_accepted flips the call active (and
      // starts the timer) only when the customer actually joins.
      await WebCallClient.connect(data.connection, data.call.id);
      return createdCall;
    } catch (error) {
      if (createdCall) {
        // create() succeeded but connect() failed mid-join — the store already
        // has a ringing call and FloatingCallWidget would show an outgoing card
        // for a room the agent never reached. Tear it all down so the failure
        // looks like the call never started. Any clip captured before the
        // failure is discarded — it isn't the full call.
        WebCallClient.discardRecording();
        try {
          // Best-effort: connect() may have left a half-joined room;
          // don't let a failed leave() block the store cleanup below.
          await WebCallClient.disconnect();
        } catch (_) {
          /* noop */
        }
        // removeCall() re-runs teardownByProvider (disconnect again), which is
        // idempotent — safe to call after the explicit disconnect above.
        callsStore.removeCall(createdCall.provider_call_id);
        // Best-effort: tell the backend so the call record doesn't keep
        // ringing/billing after the agent's connect failed locally.
        await WebCallsAPI.terminate(createdCall.id).catch(() => {});
      }
      useAlert(
        error?.response?.data?.error || t('CONVERSATION.WEB_CALL.CREATE_ERROR')
      );
      return null;
    } finally {
      isStarting.value = false;
    }
  };

  return { start, isStarting };
};
